import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, ArrowLeft, ShieldCheck, BookOpen, MessageSquare, Users, Flame, Trophy,
  Medal, ExternalLink, Crown, ChevronRight, Search, BadgePercent, Coins, ShoppingBag, Upload, Wallet,
} from "lucide-react";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Ticker from "../components/Ticker";
import FirmCard from "../components/FirmCard";
import FirmIcon from "../components/FirmIcon";
import StarRating from "../components/StarRating";
import DiscountBadge from "../components/DiscountBadge";
import OfferPopup from "../components/OfferPopup";
import FaqSection from "../components/FaqSection";
import Reveal, { SectionLabel } from "../components/Reveal";

function MaskedLine({ children, delay, gradient = false }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block font-display text-5xl font-900 leading-[1.02] tracking-tight sm:text-7xl lg:text-8xl ${gradient ? "text-gradient" : "text-white"}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function FeaturedSwitcher({ firms }) {
  const featured = firms.filter((f) => f.featured);
  const [activeSlug, setActiveSlug] = useState(null);
  const active = featured.find((f) => f.slug === activeSlug) || featured[0];
  if (!active) return null;
  const topRated = featured.find((f) => f.pinned) || featured.reduce((a, b) => (b.rating > a.rating ? b : a), featured[0]);

  return (
    <div data-testid="featured-switcher">
      <div className="flex flex-wrap gap-2.5">
        {featured.map((f) => (
          <button
            key={f.id}
            onClick={() => setActiveSlug(f.slug)}
            data-testid={`switch-${f.slug}`}
            className={`rounded-full border px-5 py-2.5 font-display text-sm font-700 transition-all duration-200 ${
              active.slug === f.slug
                ? "border-signal/60 bg-signal/15 text-white shadow-[0_0_20px_rgba(0,212,255,0.25)]"
                : "border-line bg-surface text-muted hover:border-signal/40 hover:text-white"
            }`}
          >
            {f.name}
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="card-violet mt-6 p-7 sm:p-9"
          data-testid="featured-panel"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-line bg-elevate">
                <FirmIcon name={active.icon} size={30} logo={active.logo} />
              </div>
              <div>
                <h3 className="font-display text-2xl font-800 tracking-tight text-white sm:text-3xl">{active.name}</h3>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-ink px-3 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-muted">
                    {active.platforms.slice(0, 3).join(", ")}
                  </span>
                  {active.discount_code && (
                    <DiscountBadge label={active.discount_label} code={active.discount_code} slug={`feat-${active.slug}`} compact />
                  )}
                </div>
              </div>
            </div>
            {active.id === topRated.id && (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 font-mono text-[10px] font-600 uppercase tracking-[0.12em] ${active.pinned ? "border-gold/50 bg-gold/10 text-gold" : "border-signal/40 bg-signal/10 text-signal"}`} data-testid="featured-crown">
                <Crown size={13} /> {active.pinned ? "Highest Rated" : "#1 Rated"}
              </span>
            )}
          </div>
          <div className="mt-7 grid grid-cols-2 gap-3 rounded-2xl border border-line bg-ink/60 p-5 sm:grid-cols-3">
            <div className="col-span-2 sm:col-span-1">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">Rating</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="font-mono text-lg font-700 text-white sm:text-xl">{active.rating.toFixed(1)}</span>
                <StarRating value={active.rating} idPrefix={`feat-${active.slug}`} size={14} />
              </div>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">Max Allocation</p>
              <p className="mt-2 font-mono text-lg font-700 text-white sm:text-xl">{active.max_capital}</p>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">Profit Split</p>
              <p className="mt-2 font-mono text-lg font-700 text-signal sm:text-xl">{active.profit_split}</p>
            </div>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <a href={active.website_url} target="_blank" rel="noopener noreferrer sponsored"
              data-testid="featured-get-offer"
              className="btn-beam btn-gradient inline-flex items-center gap-2 px-7 py-3.5 font-display text-sm font-800 text-white">
              Get Offer <ExternalLink size={14} />
            </a>
            <Link to={`/firms/${active.slug}`} data-testid="featured-read-review"
              className="inline-flex items-center gap-2 rounded-full border border-line px-7 py-3.5 font-mono text-[11px] font-600 uppercase tracking-[0.15em] text-white transition-colors hover:border-signal/60 hover:text-signal">
              Read Full Review <ChevronRight size={13} />
            </Link>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function OffersCarousel({ firms }) {
  const offerFirms = firms.filter((f) => f.discount_code);
  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < offerFirms.length; i += 4) out.push(offerFirms.slice(i, i + 4));
    return out;
  }, [offerFirms]);
  const [page, setPage] = useState(0);
  const month = new Date().toLocaleString("en-US", { month: "long" });
  if (!pages.length) return null;
  const total = pages.length;

  return (
    <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="offers-section">
      <Reveal>
        <div className="flex flex-wrap items-center justify-between gap-5">
          <h2 className="flex items-center gap-3 font-display text-3xl font-800 tracking-tight text-white sm:text-4xl">
            Exclusive {month} Offers <Flame className="text-signal" size={30} />
          </h2>
          <div className="flex items-center gap-3">
            <button onClick={() => setPage((page - 1 + total) % total)} data-testid="offers-prev"
              aria-label="Previous offers"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-muted transition-colors hover:border-signal/60 hover:text-signal">
              <ArrowLeft size={16} />
            </button>
            <div className="flex gap-1.5">
              {pages.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === page ? "w-6 bg-signal" : "w-1.5 bg-line"}`} />
              ))}
            </div>
            <button onClick={() => setPage((page + 1) % total)} data-testid="offers-next"
              aria-label="Next offers"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-muted transition-colors hover:border-signal/60 hover:text-signal">
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </Reveal>
      <div className="mt-8 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={page}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-1 gap-3.5"
            data-testid={`offers-page-${page}`}
          >
            {pages[page].map((f) => (
              <div key={f.id} className="card-violet flex items-center justify-between gap-4 px-5 py-4 sm:px-7" data-testid={`offer-row-${f.slug}`}>
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-line bg-elevate">
                    <FirmIcon name={f.icon} logo={f.logo} size={22} />
                  </div>
                  <div className="min-w-0">
                    <Link to={`/firms/${f.slug}`} className="block truncate font-display text-base font-700 text-white transition-colors hover:text-signal sm:text-lg">
                      {f.name}
                    </Link>
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="font-mono text-xs font-700 text-gold">{f.rating.toFixed(1)}</span>
                      <StarRating value={f.rating} idPrefix={`offer-${f.slug}`} size={12} />
                    </div>
                  </div>
                </div>
                <DiscountBadge label={f.discount_label} code={f.discount_code} slug={`row-${f.slug}`} />
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

function PopularFirms({ firms }) {
  const top = [...firms].sort((a, b) => (b.pinned === true) - (a.pinned === true) || b.rating - a.rating).slice(0, 3);
  const medals = ["#FBBF24", "#C9CED6", "#D08A4E"];
  if (!top.length) return null;
  return (
    <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8" data-testid="popular-section">
      <Reveal>
        <h2 className="flex items-center gap-3 font-display text-3xl font-800 tracking-tight text-white sm:text-4xl">
          Most Popular Prop Firms <Trophy className="text-gold" size={30} />
        </h2>
      </Reveal>
      <div className="mt-8 grid grid-cols-1 gap-3.5">
        {top.map((f, i) => (
          <Reveal key={f.id} delay={i * 0.07}>
            <div className="card-violet flex items-center justify-between gap-4 px-5 py-5 sm:px-7" data-testid={`popular-row-${f.slug}`}>
              <div className="flex min-w-0 items-center gap-4">
                <Medal size={26} style={{ color: medals[i] }} className="shrink-0" />
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-line bg-elevate">
                  <FirmIcon name={f.icon} logo={f.logo} size={22} />
                </div>
                <div className="min-w-0">
                  <Link to={`/firms/${f.slug}`} className="block truncate font-display text-base font-700 text-white transition-colors hover:text-signal sm:text-lg">
                    {f.name}
                  </Link>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="font-mono text-xs font-700 text-gold">{f.rating.toFixed(1)}</span>
                    <StarRating value={f.rating} idPrefix={`pop-${f.slug}`} size={12} />
                  </div>
                </div>
              </div>
              {f.discount_code && <DiscountBadge label={f.discount_label} code={f.discount_code} slug={`pop-${f.slug}`} />}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function PayoutWall({ proofs }) {
  if (!proofs.length) return null;
  return (
    <section className="border-y border-line bg-surface/40 py-20" data-testid="payout-wall">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="text-center">
            <SectionLabel>Proof Over Promises</SectionLabel>
            <h2 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
              The verified payout <span className="text-gradient">wall</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted">
              Real member withdrawals, checked by the desk against original screenshots.
              Tap any certificate to open the firm that paid.
            </p>
          </div>
        </Reveal>
      </div>
      <div className="mt-12">
        <Marquee speed={22} gradient={false} pauseOnHover>
          {proofs.map((p) => (
            <Link key={p.id} to={`/firms/${p.firm_slug || ""}`} data-testid={`wall-proof-${p.id}`}
              className="card-violet group mx-4 block w-72 overflow-hidden">
              <div className="relative h-44 overflow-hidden">
                <img
                  src={`${api.defaults.baseURL}/payout-proofs/file/${p.id}`}
                  alt={`${p.firm_name} payout proof by ${p.user_name}`}
                  className="h-full w-full object-cover opacity-90 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100"
                />
                <span className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border border-signal/50 bg-black/70 px-3 py-1 font-mono text-[9px] font-700 uppercase tracking-[0.12em] text-signal backdrop-blur">
                  <ShieldCheck size={11} /> Verified
                </span>
              </div>
              <div className="flex items-center justify-between p-4">
                <div className="min-w-0">
                  <p className="truncate font-mono text-xs font-700 text-white">{p.user_name}</p>
                  <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted">{p.firm_name}</p>
                </div>
                <p className="shrink-0 font-mono text-sm font-700 text-signal">{p.amount || "PAID"}</p>
              </div>
            </Link>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

function LogoMarquee({ firms }) {
  return (
    <div className="border-b border-line bg-surface/50 py-8" data-testid="logo-marquee">
      <Marquee speed={22} gradient={false} pauseOnHover>
        {firms.map((f) => (
          <Link key={f.id} to={`/firms/${f.slug}`} data-testid={`logo-marquee-${f.slug}`}
            className="mx-5 flex h-20 w-36 items-center justify-center gap-2.5 rounded-2xl border border-line bg-elevate/60 px-4 transition-colors duration-200 hover:border-signal/50">
            <FirmIcon name={f.icon} logo={f.logo} size={20} />
            <span className="truncate font-display text-xs font-700 text-muted">{f.name}</span>
          </Link>
        ))}
      </Marquee>
    </div>
  );
}

const CATS = ["All", "CFD", "Futures", "Crypto"];

function CompareTable({ firms }) {
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("rank");
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    let list = [...firms];
    if (cat !== "All") list = list.filter((f) => (f.category || "CFD") === cat);
    if (query) list = list.filter((f) => f.name.toLowerCase().includes(query.toLowerCase()));
    if (sort === "rank" || sort === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sort === "reviews") list.sort((a, b) => (b.review_count || 0) - (a.review_count || 0));
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    list.sort((a, b) => (b.pinned === true) - (a.pinned === true));
    return list;
  }, [firms, cat, sort, query]);

  return (
    <section className="border-b border-line bg-surface/40" data-testid="compare-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <Reveal>
          <SectionLabel>The Index</SectionLabel>
          <h2 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
            Compare every firm
          </h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="mt-10 grid grid-cols-2 gap-2 rounded-2xl border border-line bg-ink p-2 sm:grid-cols-4" data-testid="compare-cats">
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`cat-${c.toLowerCase()}`}
                className={`rounded-xl px-4 py-2.5 font-display text-sm font-700 transition-all duration-200 ${
                  cat === c ? "bg-signal text-white shadow-[0_0_18px_rgba(0,212,255,0.35)]" : "text-muted hover:text-white"
                }`}>
                {c}
              </button>
            ))}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-[220px_1fr]">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="compare-sort"
              className="rounded-xl border border-line bg-ink px-4 py-3 font-mono text-xs text-white outline-none focus:border-signal">
              <option value="rank">Sort by Rank</option>
              <option value="reviews">Sort by Reviews</option>
              <option value="name">Sort A–Z</option>
            </select>
            <div className="relative">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="compare-search"
                placeholder="Search firm name..."
                className="w-full rounded-xl border border-line bg-ink py-3 pl-11 pr-4 text-sm text-white outline-none transition-colors focus:border-signal" />
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-6 overflow-x-auto rounded-2xl border border-line" data-testid="compare-table">
            <table className="w-full min-w-[880px] border-collapse font-mono text-xs">
              <thead>
                <tr className="border-b border-line bg-elevate text-left text-[10px] uppercase tracking-[0.2em] text-muted">
                  <th className="px-5 py-4 font-500">Firm</th>
                  <th className="px-5 py-4 font-500">Rating / Reviews</th>
                  <th className="px-5 py-4 font-500">Country</th>
                  <th className="px-5 py-4 font-500">Founded</th>
                  <th className="px-5 py-4 font-500">Max Capital</th>
                  <th className="px-5 py-4 font-500">Split</th>
                  <th className="px-5 py-4 font-500">Discount</th>
                  <th className="px-5 py-4 font-500"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-10 text-center text-muted" data-testid="compare-empty">No firms match this filter.</td></tr>
                )}
                {rows.map((f, i) => (
                  <tr key={f.id} className="border-b border-line/50 text-muted transition-colors duration-200 hover:bg-signal/[0.04]" data-testid={`compare-row-${f.slug}`}>
                    <td className="px-5 py-4">
                      <Link to={`/firms/${f.slug}`} className="flex items-center gap-3 text-white hover:text-signal">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] font-mono text-[10px] font-800 text-white" data-testid={`compare-rank-${f.slug}`}>{i + 1}</span>
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-elevate"><FirmIcon name={f.icon} logo={f.logo} size={15} /></span>
                        <span className="font-600">{f.name}</span>
                      </Link>
                    </td>
                    <td className="px-5 py-4">
                      <span className="flex items-center gap-2.5">
                        <span className="rounded-lg border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 px-2.5 py-1 font-700 text-[#C4B5FD]">{f.rating.toFixed(1)}</span>
                        <StarRating value={f.rating} idPrefix={`cmp-${f.slug}`} size={11} tone="violet" />
                        <span className="text-[10px]">({f.review_count || 0})</span>
                      </span>
                    </td>
                    <td className="px-5 py-4">{f.country || "—"}</td>
                    <td className="px-5 py-4">{f.founded}</td>
                    <td className="px-5 py-4 text-white">{f.max_capital}</td>
                    <td className="px-5 py-4 text-white">{f.profit_split}</td>
                    <td className="px-5 py-4">
                      {f.discount_code
                        ? <DiscountBadge label={f.discount_label} code={f.discount_code} slug={`cmp-${f.slug}`} compact />
                        : "—"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <a href={f.website_url} target="_blank" rel="noopener noreferrer sponsored" data-testid={`compare-visit-${f.slug}`}
                        className="btn-gradient inline-flex items-center gap-1.5 px-4 py-2 text-[10px] font-700 uppercase tracking-[0.12em] text-white">
                        Get Offer <ExternalLink size={10} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const [firms, setFirms] = useState([]);
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [proofs, setProofs] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const blobY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroFade = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  useEffect(() => {
    api.get("/firms").then(({ data }) => setFirms(data)).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 3))).catch(() => {});
    api.get("/payout-proofs/verified").then(({ data }) => setProofs(data)).catch(() => {});
  }, []);

  const statPills = [
    { icon: ShieldCheck, v: stats ? `${stats.firms}` : "—", l: "Verified Prop Firms" },
    { icon: BookOpen, v: `${firms.reduce((s, f) => s + (f.challenges?.length || 0), 0)}+`, l: "Challenge Accounts" },
    { icon: MessageSquare, v: stats ? `${stats.reviews}+` : "—", l: "Real Trader Reviews" },
    { icon: Users, v: stats ? `${stats.traders}+` : "—", l: "Club Members" },
  ];

  return (
    <div data-testid="home-page">
      <Header />
      <OfferPopup firms={firms} />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <div className="scanlines-overlay" />
        <motion.div style={{ y: blobY }} className="absolute -left-32 top-24 h-96 w-96 rounded-full bg-[#8B5CF6]/20 blur-[150px]" />
        <motion.div style={{ y: blobY }} className="absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-[#3B82F6]/20 blur-[140px]" />
        <div className="absolute left-1/2 top-1/3 h-72 w-[42rem] -translate-x-1/2 rounded-full bg-[#8B5CF6]/10 blur-[160px]" />

        <motion.div style={{ opacity: heroFade }} className="relative mx-auto flex max-w-7xl flex-col justify-center px-5 pb-16 pt-14 text-center lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="mx-auto mb-8 inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#C4B5FD]"
            data-testid="hero-eyebrow"
          >
            <ShieldCheck size={12} /> Independent · Verified · Trader-First
          </motion.div>
          <h1 data-testid="hero-heading">
            <MaskedLine delay={0.25}>EVERY PROP FIRM.</MaskedLine>
            <MaskedLine delay={0.4} gradient>ONE HONEST</MaskedLine>
            <MaskedLine delay={0.55}>VERDICT.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-muted sm:text-lg"
          >
            Funded Traders Club scores, compares and discounts the sharpest prop firms — verified
            payout data, member-only reviews and one universal code: <span className="font-mono font-700 text-signal">FTC</span>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-10 flex flex-wrap justify-center gap-4"
          >
            <Link to="/firms" data-testid="hero-cta-rankings"
              className="btn-beam btn-gradient inline-flex items-center gap-2 px-8 py-4 font-display text-sm font-800 text-white">
              Browse Firms <ArrowRight size={15} />
            </Link>
            <Link to="/blog" data-testid="hero-cta-journal"
              className="inline-flex items-center gap-2 rounded-full border border-line px-8 py-4 font-mono text-xs font-600 uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-signal/60 hover:text-signal">
              Trading Articles
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mx-auto mt-8 flex max-w-2xl items-center gap-3.5 rounded-2xl border border-gold/50 bg-gold/10 px-7 py-5 shadow-[0_0_50px_rgba(245,197,66,0.15)]"
            data-testid="hero-cashback"
          >
            <BadgePercent size={26} className="shrink-0 text-gold" />
            <p className="text-left font-display text-base font-700 leading-snug text-gold sm:text-lg">
              Use code <span className="font-800 underline decoration-gold/60 underline-offset-4">FTC</span> to buy an account at any firm — whatever commission we receive from your sale comes straight back to you as cashback, up to 15%.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-3 lg:grid-cols-4"
            data-testid="hero-stats"
          >
            {statPills.map((s) => (
              <div key={s.l} className="card-violet flex items-center gap-3 px-5 py-4 text-left">
                <s.icon size={18} className="shrink-0 text-signal" />
                <div>
                  <p className="font-mono text-sm font-700 text-white">{s.v}</p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-muted">{s.l}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Ticker firms={firms} />

      <section className="relative border-b border-line" data-testid="cashback-how-section">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Reveal>
            <div className="text-center">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal">{"// How Cashback Works"}</p>
              <h2 className="mt-3 font-display text-3xl font-800 tracking-tight text-white sm:text-4xl">
                Buy with <span className="text-gradient">FTC</span>. Get paid back.
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted sm:text-base">
                Whatever commission we receive from your purchase comes straight back to you as FTC coins — up to 15% cashback on every account.
              </p>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: BadgePercent, n: "01", t: "Copy code FTC", d: "One universal member code — it works at checkout on every firm listed here.", testid: "cashback-step-1" },
              { icon: ShoppingBag, n: "02", t: "Buy any account", d: "Pick any firm, choose your account size and apply code FTC at checkout.", testid: "cashback-step-2" },
              { icon: Upload, n: "03", t: "Claim in your wallet", d: "Open your FTC Wallet, submit a claim with your order details and screenshot.", testid: "cashback-step-3" },
              { icon: Coins, n: "04", t: "Earn FTC coins", d: "The desk verifies your order and credits coins — 1 coin = $1, withdraw via UPI or crypto.", testid: "cashback-step-4" },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="card-violet group relative h-full p-6 transition-transform duration-300 hover:-translate-y-1" data-testid={s.testid}>
                  <span className="absolute right-5 top-5 font-mono text-xs font-700 text-signal/50">{s.n}</span>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/40 bg-gold/10 text-gold transition-colors duration-300 group-hover:bg-gold/20">
                    <s.icon size={19} />
                  </span>
                  <p className="mt-4 font-display text-base font-800 text-white">{s.t}</p>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.35}>
            <div className="mt-8 text-center">
              <Link to="/wallet" data-testid="cashback-cta-wallet"
                className="btn-beam btn-gradient inline-flex items-center gap-2 px-8 py-4 font-display text-sm font-800 text-white">
                <Wallet size={15} /> Claim Your Cashback
              </Link>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                Free account needed — claims are verified by the desk
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED SWITCHER */}
      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="featured-section">
        <Reveal>
          <SectionLabel>Top Rated This Quarter</SectionLabel>
          <h2 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
            The desks clearing our bar
          </h2>
        </Reveal>
        <div className="mt-10">
          <FeaturedSwitcher firms={firms} />
        </div>
      </section>

      <OffersCarousel firms={firms} />
      <PopularFirms firms={firms} />
      <PayoutWall proofs={proofs} />
      <LogoMarquee firms={firms} />
      <CompareTable firms={firms} />

      {/* WHY TRUST */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="methodology-section">
        <Reveal>
          <div className="text-center">
            <SectionLabel>Why Traders Trust The Club</SectionLabel>
            <h2 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
              Built different, on purpose
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {[
            { icon: ShieldCheck, t: "Scores can't be bought", d: "Referral revenue funds the club. It never touches the rating algorithm — payouts, conditions and rule history set the score." },
            { icon: Flame, t: "One universal code", d: "Code FTC works at every firm on the index. No hunting coupon sites — the discount lives here." },
            { icon: Users, t: "Verified member signal", d: "Reviews come from real member accounts, and payout certificates prove who actually got paid." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className="card-violet h-full p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-signal/10">
                  <c.icon size={22} className="text-signal" />
                </div>
                <h3 className="mt-5 font-display text-xl font-700 tracking-tight text-white">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FaqSection />

      {/* ARTICLES PREVIEW */}
      {posts.length > 0 && (
        <section className="border-t border-line bg-surface/40" data-testid="journal-preview">
          <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-6">
                <div>
                  <SectionLabel>Insights & Analysis</SectionLabel>
                  <h2 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl">
                    Latest prop firm articles & news
                  </h2>
                </div>
                <Link to="/blog" data-testid="journal-view-all"
                  className="inline-flex items-center gap-2 rounded-full border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:border-signal/60 hover:text-signal">
                  View All Articles <ArrowRight size={13} />
                </Link>
              </div>
            </Reveal>
            <div className="mt-12 grid gap-4 md:grid-cols-3">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <Link to={`/blog/${p.slug}`} className="card-violet group block h-full overflow-hidden" data-testid={`journal-card-${p.slug}`}>
                    <div className="overflow-hidden">
                      <img src={p.cover_image} alt="" className="aspect-[16/9] w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100" />
                    </div>
                    <div className="p-6">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{p.category} · {p.read_time}</p>
                      <h3 className="mt-3 font-display text-lg font-700 leading-snug tracking-tight text-white transition-colors group-hover:text-signal">{p.title}</h3>
                      <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-muted">{p.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-line" data-testid="cta-section">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute left-1/2 top-1/2 h-72 w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-signal/15 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-28 text-center lg:px-8">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal">{"// Execute"}</p>
            <h2 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-900 leading-[1.05] tracking-tight text-white sm:text-6xl">
              Your evaluation fee is capital. <span className="text-gradient">Spend it with an edge.</span>
            </h2>
            <Link to="/firms" data-testid="cta-browse-firms"
              className="btn-beam btn-gradient mt-10 inline-flex items-center gap-2 px-9 py-4 font-display text-sm font-800 text-white">
              Browse All Firms <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
